import type { ComposeTab } from '../composeActions';

/**
 * Conversation actions — workflow templates inserted into the textarea
 * for the operator to review/edit before sending. All kind:'insert'
 * (no fireOverride flags in rung 2 per spec-triad YAGNI ruling).
 */
const focusSessionPrompt = `Let's run a focused working session.

Protocol:
1. Ask me **one** clarifying question at a time — never a wall of questions
2. Wait for my answer before the next question
3. Continue until you have enough to produce a concrete deliverable (plan, draft, decision doc, code, whatever fits)
4. When you have enough, say "I have what I need" and produce it
5. If I go off-topic, gently pull us back

Start by asking me: **what's the deliverable, and when do I need it by?**`;

const briefNextPersonPrompt = `Write a handoff note for another persona picking up this thread cold. Format:

**Handoff: <one-line topic>**

1. **Where we are** — the state of the work, in 2–3 sentences
2. **What's decided** — bullet list of decisions already made (with my reasoning for each)
3. **What's open** — bullet list of unresolved questions or choices
4. **What they need to know about me** — preferences, constraints, context that will make them effective from minute one
5. **Recommended next move** — what you'd do first if you were continuing

Keep it copy-pasteable. Minute-one effectiveness is the goal.`;

const failureModesPrompt = `What's the most likely way our current approach goes wrong?

List the top 3 failure modes:
1. **Mode** + a one-line description of what triggers it
2. **Severity** (critical / major / minor) + what we lose if it happens
3. **What would prevent it** — concrete, actionable

End with the single failure mode you'd most want to defend against first, and why.`;

export const conversationActionsTab: ComposeTab = {
  id: 'actions',
  emoji: '📝',
  label: 'Actions',
  descriptor: 'Workflow templates — review + edit + send',
  actions: [
    {
      kind: 'insert',
      id: 'action-summarize',
      label: '📋 Summarize conversation',
      descriptor: 'Key topics, decisions made, open questions',
      prompt:
        'Please summarize our entire conversation so far. Include: key topics discussed, '
        + 'decisions made, and any open questions.',
    },
    {
      kind: 'insert',
      id: 'action-extract-actions',
      label: '✅ Extract action items',
      descriptor: 'Action items + decisions + next steps as a checklist',
      prompt:
        'Extract all action items, decisions, and concrete next steps from our '
        + 'conversation. Format as a checklist.',
    },
    {
      kind: 'insert',
      id: 'action-probe-questions',
      label: '⚡ Generate probe questions',
      descriptor: '3-5 questions that test understanding of what we discussed',
      prompt:
        'Based on our conversation, generate 3-5 probe questions that would test '
        + 'whether a persona truly understands the concepts we discussed.',
    },
    {
      kind: 'insert',
      id: 'action-focus-session',
      label: '🎯 Focus Session',
      descriptor: 'One question at a time until deliverable is producible',
      prompt: focusSessionPrompt,
    },
    {
      kind: 'insert',
      id: 'action-brief-next',
      label: '📝 Brief Next Person',
      descriptor: 'Copy-pasteable handoff note for next persona',
      prompt: briefNextPersonPrompt,
    },
    {
      kind: 'insert',
      id: 'action-failure-modes',
      label: '🪤 Failure Modes',
      descriptor: 'Top 3 ways the current approach goes wrong',
      prompt: failureModesPrompt,
    },
  ],
};
